import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Box, FormControlLabel, Radio, Stack, Typography } from '@mui/material';
import { citysprintVehicleCardDisplayTitle } from '../helpers/vehicleTypeCards';

const CITYSPRINT_VEHICLE_ICON_BLACK_FILTER = 'brightness(0)';
const VEHICLE_CARD_SPEC_COLOR = '#666666';
const VEHICLE_CARD_SPEC_FONT_SIZE = '0.75rem';
const VEHICLE_CARD_BORDER_RADIUS = 1.5;

function formatInlineDimensionText(vehicle) {
  const lines = vehicle.dimensionLines;
  if (Array.isArray(lines) && lines.length) {
    return lines.map(line => line.text).join(' ');
  }
  return vehicle.dimensions ?? vehicle.dimensionsCaption ?? '';
}

function VehicleIcon({ vehicle, size = 48, sx }) {
  if (vehicle.vehicleIconUrl) {
    return (
      <Box
        alt=''
        component='img'
        decoding='async'
        loading='lazy'
        referrerPolicy='no-referrer'
        src={vehicle.vehicleIconUrl}
        sx={theme => ({
          width: size,
          height: size,
          objectFit: 'contain',
          filter: CITYSPRINT_VEHICLE_ICON_BLACK_FILTER,
          flexShrink: 0,
          [theme.getColorSchemeSelector('dark')]: {
            filter: 'brightness(0) invert(1)',
          },
          ...sx,
        })}
      />
    );
  }

  return (
    <LocalShippingIcon
      sx={{
        fontSize: size * 0.75 * (vehicle.iconScale ?? 1),
        maxWidth: size + 8,
        color: 'text.primary',
        flexShrink: 0,
        ...sx,
      }}
    />
  );
}

function VehicleDimensionLines({
  dimensionLines,
  fallbackText,
  align = 'center',
}) {
  const isLeft = align === 'left';

  if (dimensionLines.length) {
    return (
      <Stack
        spacing={0.35}
        sx={{
          mb: 0.5,
          alignItems: isLeft ? 'flex-start' : 'center',
        }}
      >
        {dimensionLines.map(line => (
          <Typography
            key={line.axis}
            variant='body2'
            sx={{
              fontSize: VEHICLE_CARD_SPEC_FONT_SIZE,
              lineHeight: 1.35,
              color: 'text.primary',
              textAlign: align,
            }}
          >
            {line.text}
          </Typography>
        ))}
      </Stack>
    );
  }

  return (
    <Typography
      variant='body2'
      sx={{
        fontSize: VEHICLE_CARD_SPEC_FONT_SIZE,
        lineHeight: 1.35,
        color: 'text.primary',
        mb: 0.5,
        textAlign: align,
      }}
    >
      {fallbackText}
    </Typography>
  );
}

function VehicleMaxWeightText({ label, sx }) {
  return (
    <Typography
      variant='body2'
      sx={{
        fontSize: VEHICLE_CARD_SPEC_FONT_SIZE,
        lineHeight: 1.35,
        color: VEHICLE_CARD_SPEC_COLOR,
        textAlign: 'center',
        ...sx,
      }}
    >
      {label}
    </Typography>
  );
}

function vehicleCardBorderSx(selected) {
  return {
    borderRadius: VEHICLE_CARD_BORDER_RADIUS,
    borderStyle: 'solid',
    borderColor: selected ? 'primary.main' : 'divider',
    borderWidth: selected ? 2 : 1,
    bgcolor: 'background.paper',
    transition: theme =>
      theme.transitions.create('border-color', { duration: 150 }),
    '&:hover': {
      borderColor: 'primary.main',
    },
  };
}

function CollectionVehicleCardVertical({
  vehicle,
  title,
  maxWeightLabel,
  dimensionLines,
  inlineDimensions,
}) {
  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        pt: 1.5,
        px: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 48,
          mb: 1.25,
        }}
      >
        <VehicleIcon vehicle={vehicle} size={48} />
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography
          component='span'
          sx={{
            display: 'block',
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: 1.25,
            color: 'text.primary',
            mb: 0.75,
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>
        <VehicleDimensionLines
          dimensionLines={dimensionLines}
          fallbackText={inlineDimensions}
          align='center'
        />
        <VehicleMaxWeightText label={maxWeightLabel} />
      </Box>
    </Box>
  );
}

function CollectionVehicleCardMobile({
  vehicle,
  title,
  maxWeightLabel,
  inlineDimensions,
}) {
  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        py: 1.25,
        pl: 1.5,
        pr: 0.75,
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.5,
        }}
      >
        <VehicleIcon vehicle={vehicle} size={32} />
        <Typography
          component='span'
          sx={{
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: 1.25,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant='body2'
        sx={{
          fontSize: VEHICLE_CARD_SPEC_FONT_SIZE,
          lineHeight: 1.35,
          color: 'text.primary',
          mb: 0.35,
        }}
      >
        {inlineDimensions}
      </Typography>
      <VehicleMaxWeightText label={maxWeightLabel} sx={{ textAlign: 'left' }} />
    </Box>
  );
}

function VehicleCardHorizontalSpecs({ inlineDimensions, maxWeightLabel }) {
  return (
    <>
      <Typography
        variant='body2'
        sx={{
          fontSize: VEHICLE_CARD_SPEC_FONT_SIZE,
          lineHeight: 1.35,
          color: 'text.primary',
          mb: 0.35,
          textAlign: 'left',
        }}
      >
        {inlineDimensions}
      </Typography>
      <VehicleMaxWeightText label={maxWeightLabel} sx={{ textAlign: 'left' }} />
    </>
  );
}

function CollectionVehicleCardHorizontal({
  vehicle,
  title,
  maxWeightLabel,
  inlineDimensions,
}) {
  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        py: 2,
        pl: 2,
        pr: 1,
      }}
    >
      <VehicleIcon vehicle={vehicle} size={44} />
      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          pr: 2,
          textAlign: 'left',
        }}
      >
        <Typography
          component='span'
          sx={{
            display: 'block',
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: 1.25,
            color: 'text.primary',
            mb: 0.75,
          }}
        >
          {title}
        </Typography>
        <VehicleCardHorizontalSpecs
          inlineDimensions={inlineDimensions}
          maxWeightLabel={maxWeightLabel}
        />
      </Box>
    </Box>
  );
}

export function CollectionVehicleCard({
  vehicle,
  vehicleIndex,
  orientation,
  selected,
}) {
  const title = citysprintVehicleCardDisplayTitle(vehicle.name);
  const useHorizontalLayout = orientation === 'horizontal';
  const maxWeightLabel =
    typeof vehicle.maxKg === 'number'
      ? `Max ${vehicle.maxKg}kg`
      : `Max ${vehicle.maxKg}`;
  const dimensionLines = Array.isArray(vehicle.dimensionLines)
    ? vehicle.dimensionLines
    : [];
  const inlineDimensions = formatInlineDimensionText(vehicle);

  return (
    <FormControlLabel
      value={String(vehicleIndex)}
      control={
        <Radio
          sx={{
            m: 0,
            p: { xs: 0.75, sm: 0.75 },
            flexShrink: 0,
            alignSelf: {
              xs: 'center',
              sm: useHorizontalLayout ? 'flex-start' : 'center',
            },
            mt: { sm: useHorizontalLayout ? 1.25 : 0 },
            mr: { sm: useHorizontalLayout ? 1.25 : 0 },
            py: { xs: 0, sm: useHorizontalLayout ? 0 : 1.25 },
          }}
        />
      }
      label={
        <Box sx={{ width: '100%', flex: 1, display: 'flex' }}>
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              width: '100%',
              flex: 1,
            }}
          >
            <CollectionVehicleCardMobile
              vehicle={vehicle}
              title={title}
              maxWeightLabel={maxWeightLabel}
              inlineDimensions={inlineDimensions}
            />
          </Box>
          <Box
            sx={{
              display: {
                xs: 'none',
                sm: useHorizontalLayout ? 'none' : 'flex',
              },
              width: '100%',
              flex: 1,
            }}
          >
            <CollectionVehicleCardVertical
              vehicle={vehicle}
              title={title}
              maxWeightLabel={maxWeightLabel}
              dimensionLines={dimensionLines}
              inlineDimensions={inlineDimensions}
            />
          </Box>
          {useHorizontalLayout ? (
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                width: '100%',
                flex: 1,
              }}
            >
              <CollectionVehicleCardHorizontal
                vehicle={vehicle}
                title={title}
                maxWeightLabel={maxWeightLabel}
                inlineDimensions={inlineDimensions}
              />
            </Box>
          ) : null}
        </Box>
      }
      sx={{
        m: 0,
        p: 0,
        height: '100%',
        width: '100%',
        alignItems: 'stretch',
        cursor: 'pointer',
        flexDirection: {
          xs: 'row-reverse',
          sm: useHorizontalLayout ? 'row-reverse' : 'column-reverse',
        },
        ...vehicleCardBorderSx(selected),
        '& .MuiFormControlLabel-label': {
          m: 0,
          width: '100%',
          flex: 1,
          display: 'flex',
        },
      }}
    />
  );
}
